import {Component, inject, Input, Output, EventEmitter, OnInit, OnDestroy, OnChanges, SimpleChanges} from '@angular/core';
import {userList, Wars} from "../../interfaces/account-data";
import {takeUntil} from "rxjs/operators";
import {Delivery} from "../../interfaces/delivery";
import {HttpClient} from "@angular/common/http";
import {BaseService} from "../../services/base.service";
import {Subject} from "rxjs";
import {LocalStorageService} from "../../services/local-storage.service";
import {Router} from "@angular/router";
import {cloneDeep} from 'lodash';
import {Store} from "@ngrx/store";
import {setHaveGoodsStore} from "../../store/have-goods/have-goods.actions";
import {setIncludePartnersStore} from "../../store/include-partners/include-partners.action";
import {OrganizationsService} from "../../services/organizations.service";
import {Organization} from "../../interfaces/organization";
import {HttpErrorNotifyService} from "../../services/http-error-notify.service";

@Component({
  selector: 'app-navigation-menu',
  templateUrl: './navigation-menu.component.html',
  styleUrls: ['./navigation-menu.component.scss', '../../pages/account/account.component.scss'],
})

export class NavigationMenuComponent extends BaseService implements OnInit, OnChanges, OnDestroy {
  @Input() selectedUser: string;
  @Input() selectedUserAdmin: string;
  @Input() headCategoryNameColored: string;
  @Input() headCategoryName: string;
  @Input() subHeadCategoryName: string;
  @Input() userListAdmin: userList[];
  @Input() deliveryInfo: Delivery[];
  @Input() deliveryCurrent?: Delivery;
  @Input() basketSum;
  @Input() basketCount: Number;
  @Input() currencyName;
  @Input() isAdmin;
  @Input() allowLoginUsers: userList[];
  @Input() wars?: Wars[];
  @Input() showPartnerWarehouses: boolean;
  @Input() urlParams;
  @Input() categoriesSortedDeep;

  @Output() buildNavigationMenuGoodsData = new EventEmitter();
  @Output() buildNavigationMenuFavoritesData = new EventEmitter();
  @Output() buildNavigationMenuBasketData = new EventEmitter();
  @Output() brandMenuShowRef = new EventEmitter();
  @Output() hideTitleRef = new EventEmitter();

  private http = inject(HttpClient);
  private localStorage = inject(LocalStorageService);
  private router = inject(Router);
  private store: Store<{haveGoodsStore: boolean; includePartnersStore: boolean}> = inject(Store);
  private organizationsService = inject(OrganizationsService);
  private notify = inject(HttpErrorNotifyService);

  /** Вибір у селекторі «дивитись як», підтверджений беком (look_like_user) — щоб відкотити вибір, якщо бек відмовив */
  private selectedUserAdminApplied: string;
  /** Те саме для селектора «Дозволені для входу» (change_allowed_user) */
  private selectedUserApplied: string;

  public isDestroyed: Subject<boolean> = new Subject();
  public foundArray;
  public warName: string = '';
  public isMenuNpClose: boolean = false;
  public showMobileDelivery: boolean = false;
  public categoryIndex: number = 0;
  public haveGoodsCheckbox: boolean;
  public includePartners: boolean;
  public activeOrganization: Organization;
  public organizations: Organization[] = [];
  public selectedOrganizationId: number | string;
  public buEnabled: boolean = true;
  // Пункти перемикача організацій. При вимкненому «білому обліку» перемикач лишається лише у кого
  // активна ФОП/ТОВ - щоб повернутися на «Фіз особа» (оформлення по БУ бек відхиляє), картка #628
  public switchOrganizations: Organization[] = [];

  ngOnInit() {
    this.organizationsService.organizations$
      .pipe(takeUntil(this.isDestroyed))
      .subscribe((organizations: Organization[]) => {
        this.organizations = organizations;
        this.buEnabled = this.organizationsService.buEnabled;
        this.activeOrganization = organizations.find((org) => org.is_active === '1' || org.is_active === true);
        if (this.buEnabled) {
          this.switchOrganizations = organizations;
        } else if (this.activeOrganization && this.activeOrganization.type !== 'fiz') {
          this.switchOrganizations = organizations.filter((org) => org.type === 'fiz' || org === this.activeOrganization);
        } else {
          this.switchOrganizations = [];
        }

        if (!this.selectedOrganizationId) {
          const defaultOrg = this.activeOrganization || organizations.find(org => org.type === 'fiz');
          if (defaultOrg) {
            this.selectedOrganizationId = defaultOrg.id;
          }
        }
      });

    this.foundArray = cloneDeep(this.categoriesSortedDeep);

    this.store.select('haveGoodsStore')
      .pipe(takeUntil(this.isDestroyed))
      .subscribe(val => {
      this.haveGoodsCheckbox = val;
    });

    this.store.select('includePartnersStore')
      .pipe(takeUntil(this.isDestroyed))
      .subscribe(val => {
      this.includePartners = val;
    })

    this.computeWarName();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['selectedUserAdmin']) {
      this.selectedUserAdminApplied = changes['selectedUserAdmin'].currentValue;
    }

    if (changes['selectedUser']) {
      this.selectedUserApplied = changes['selectedUser'].currentValue;
    }

    // На /good меню створюється до відповіді accounts/data: доставка і склади приходять пізніше,
    // а склад у кнопці «Вибір доставки, складу» рахувався лише в ngOnInit і лишався порожнім
    if (changes['deliveryCurrent'] || changes['wars']) {
      this.computeWarName();
    }
  }

  computeWarName() {
    if (!this.deliveryCurrent || !this.wars || this.wars.length < 2) {
      this.warName = '';
      return;
    }

    this.warName = (this.deliveryCurrent.id != 2 && this.deliveryCurrent.id != 7) ? `, ${this.wars[1].name}` : '';
  }

  backToHeadCategories() {
    if (this.foundArray[this.categoryIndex].id >= '33') {
      this.headCategoryName = '';
      this.subHeadCategoryName = '';
      this.router.navigate(['account']);
      this.headCategoryNameColored = 'Головна';
    }
  }

  changeOrganization() {
    this.organizationsService.setActiveOrganization(this.selectedOrganizationId)
      .pipe(takeUntil(this.isDestroyed))
      .subscribe(() => location.reload());
  }

  changeUser() {
    const value = this.selectedUser;

    this.http.post(this.apiRoot + 'personal/change_allowed_user', {
      value
    })
      .pipe(takeUntil(this.isDestroyed))
      .subscribe((resp: {data: string; error: string}) => {
        // Бек не перелогінив (обрано «Користувач не вибраний», очищено вибір або акаунта немає у списку):
        // куки ті самі, тож не скидаємо доставку і не перезавантажуємо сторінку, а повертаємо попередній вибір.
        // «Користувач не вибраний» / очищення - не помилка користувача, тому без тосту.
        if (resp && resp.error) {
          this.selectedUser = this.selectedUserApplied;
          if (value) {
            this.notify.show('Акаунт не вибрано', 'Увійти в цей акаунт не можна. Оберіть акаунт зі списку.');
          }
          return;
        }

        this.localStorage.delete('delivery_id');
        location.reload();
      });
  }

  adminUserSelected() {
    this.http.post(this.apiRoot + 'personal/change_user', {
      value: this.selectedUserAdmin
    })
      .pipe(takeUntil(this.isDestroyed))
      .subscribe((resp: {data: string; error: string}) => {
        // Бек не підміняє цього користувача (адмін або неіснуючий): куку він не ставив, тож повертаємо
        // попередній вибір і пояснюємо, а не перезавантажуємо сторінку з тим самим балансом
        if (resp && resp.error) {
          this.selectedUserAdmin = this.selectedUserAdminApplied;
          this.notify.show('Акаунт не вибрано', 'Дивитись кабінет цього користувача не можна. Оберіть іншого акаунт зі списку.');
          return;
        }

        this.localStorage.delete('delivery_id');
        location.reload();
      });
  }

  includePartnersCheckbox() {
    this.includePartners = !this.includePartners;
    this.store.dispatch(setIncludePartnersStore({value: this.includePartners}));
    this.resetPage();

    this.router.navigate([], { queryParams: this.urlParams });

    this.buildNavigationMenuGoodsData.emit();
  }

  availabilityCheckbox() {
    this.haveGoodsCheckbox = !this.haveGoodsCheckbox
    this.store.dispatch(setHaveGoodsStore({value: this.haveGoodsCheckbox}));
    this.resetPage();

    this.router.navigate([], {queryParams: this.urlParams});

    this.buildNavigationMenuGoodsData.emit();
  }

  // Фільтр міняє кількість сторінок, як і бренд чи категорія (там сторінку теж скидають на 1):
  // зі сторінки 9 після «Наявність товару» get_goods повертав порожню сторінку, а пагінація
  // ховалась разом із товарами - клієнт бачив «Нічого не знайдено». На сторінці товару page немає.
  private resetPage() {
    if (this.urlParams && this.urlParams.page) {
      this.urlParams.page = 1;
    }
  }

  setDelivery(id) {
    this.localStorage.set('delivery_id', id);

    // Полная перезагрузка, как в модалке доставки в шапке (modal-delivery): от delivery_id зависят
    // остатки, колонка «Умова» и порядок складов, а обработчик события товаров на страницах пустой,
    // после смены доставки список оставался от старой
    location.reload();
  }

  showDelivery() {
    this.showMobileDelivery = !this.showMobileDelivery;
  }

  brandsMenuShow() {
    this.brandMenuShowRef.emit();
  }

  ngOnDestroy() {
    this.isDestroyed.next(true);
    this.isDestroyed.complete();
  }
}
